import { addDays, startOfDay, subDays } from "date-fns";
import { useCallback, useState } from "react";

import { type DateTimeSlot } from "../types";

export function useTimeSlot({
  initialValue,
  onChange,
}: {
  initialValue: DateTimeSlot;
  onChange?: (dateTimeSlot: DateTimeSlot) => void;
}) {
  const [dateTimeSlot, setDateTimeSlot] = useState(initialValue);

  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  const tomorrow = addDays(today, 1);

  const incrementTimeSlot = useCallback(() => {
    let newDateTimeSlot: DateTimeSlot;
    // eslint-disable-next-line default-case
    switch (dateTimeSlot.timeSlot) {
      case "am": {
        newDateTimeSlot = { ...dateTimeSlot, timeSlot: "pm" };
        break;
      }

      case "pm": {
        newDateTimeSlot = { ...dateTimeSlot, timeSlot: "noc" };
        break;
      }

      case "noc": {
        newDateTimeSlot = { date: addDays(dateTimeSlot.date, 1), timeSlot: "am" };
        break;
      }
    }

    // Prevent navigation beyond tomorrow
    const newDate = startOfDay(newDateTimeSlot.date);

    // Don't allow navigation past tomorrow
    if (newDate > tomorrow) {
      return;
    }

    setDateTimeSlot(newDateTimeSlot);
    onChange?.(newDateTimeSlot);
  }, [dateTimeSlot, onChange, tomorrow]);

  const decrementTimeSlot = useCallback(() => {
    let newDateTimeSlot: DateTimeSlot;
    // eslint-disable-next-line default-case
    switch (dateTimeSlot.timeSlot) {
      case "am": {
        newDateTimeSlot = { date: subDays(dateTimeSlot.date, 1), timeSlot: "noc" };
        break;
      }

      case "pm": {
        newDateTimeSlot = { ...dateTimeSlot, timeSlot: "am" };
        break;
      }

      case "noc": {
        newDateTimeSlot = { ...dateTimeSlot, timeSlot: "pm" };
        break;
      }
    }

    // Prevent navigation before yesterday
    const newDate = startOfDay(newDateTimeSlot.date);

    // Don't allow navigation before yesterday
    if (newDate < yesterday) {
      return;
    }

    setDateTimeSlot(newDateTimeSlot);
    onChange?.(newDateTimeSlot);
  }, [dateTimeSlot, onChange, yesterday]);

  // Calculate if increment is allowed
  const canIncrement = useCallback(() => {
    const currentDate = startOfDay(dateTimeSlot.date);

    // If we're on tomorrow's NOC slot, can't increment
    return !(currentDate >= tomorrow && dateTimeSlot.timeSlot === "noc");
  }, [dateTimeSlot, tomorrow]);

  // Calculate if decrement is allowed
  const canDecrement = useCallback(() => {
    const currentDate = startOfDay(dateTimeSlot.date);

    // If we're on yesterday's AM slot, can't decrement
    return !(currentDate <= yesterday && dateTimeSlot.timeSlot === "am");
  }, [dateTimeSlot, yesterday]);

  return {
    dateTimeSlot,
    incrementTimeSlot,
    decrementTimeSlot,
    canIncrement,
    canDecrement,
  } as const;
}
